import { useMemo } from 'react';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { getBrandInfo, useBrand } from './brandStore';
import { getListing, useListing } from './listingStore';

// Reconciling the manual with the web listing.
//
// Phase 1 can read the product name from two independent places: the manual's
// own pages (brandStore) and the retail listing the user pasted in Phase 1
// (listingStore). Neither is trusted over the other — a cover can be stylised past
// legibility, a listing can name the shop instead of the maker — so where the two
// disagree the USER picks, and Phase 1 does not count as done until they have.
//
// This module is the only place that knows a field has two candidates. Everything
// downstream (the part-image search, the publish snapshot) reads the resolved
// value through `useResolvedInfo` and never learns there was a choice to make.

export type Source = 'pdf' | 'listing';
export type InfoField = 'productName';

// THE BRAND WAS A FIELD HERE TOO. The listing stopped being asked for it: the manual nearly
// always prints it, and the only disagreements were a shop's name against its maker's. So the
// brand is the manual's alone, still resolved through here so downstream reads one shape.
//
// The finished-product PICTURE was a third field here. The listing stopped being a
// source for it — its photo is a gallery to look at now, not a candidate splash — so
// the manual is the only one who answers, and a question with one answer is not a
// question. loadPicks drops any 'hero' pick still in localStorage, because it walks
// this list.
export const INFO_FIELDS: InfoField[] = ['productName'];

// A pick is a decision about TWO SPECIFIC ANSWERS — "between 'Rendez Vous Deco'
// and 'Maison Pavane', keep the listing's" — not a standing preference for a
// source. So it records what was on screen when it was made: if either source
// later reads something different, that decision was about other values and the
// question is open again. This is what stops a re-run quietly swapping the brand
// under a pick the user made about something else.
type Pick = { source: Source; pdf: string; listing: string };
type Picks = Partial<Record<InfoField, Pick>>;

const key = (docId: string) => `notice.picks.${docId}`;

const isSource = (v: unknown): v is Source => v === 'pdf' || v === 'listing';

function loadPicks(docId: string): Picks {
  try {
    const o = readState<Record<string, unknown>>(docId, key(docId), {});
    if (!o || typeof o !== 'object') return {};
    // Drop anything that isn't a field this version knows about, in the shape it
    // expects. Picks written before a pick recorded its values are among those:
    // they are bare source strings, and there is no way to tell what they were
    // decided about — so they are dropped and the question asked once more.
    const out: Picks = {};
    for (const f of INFO_FIELDS) {
      const p = o[f] as Partial<Pick> | undefined;
      if (p && typeof p === 'object' && isSource(p.source) &&
          typeof p.pdf === 'string' && typeof p.listing === 'string') {
        out[f] = { source: p.source, pdf: p.pdf, listing: p.listing };
      }
    }
    return out;
  } catch {
    return {};
  }
}

const store = createDocEntryStore<Picks>((docId) => ({ snapshot: loadPicks(docId) }));

// What each candidate says right now, reduced to a string that changes whenever the
// answer does. Written into a pick and compared against on every read, so both
// sides use one definition of "the same answer".
function identities(docId: string): Record<InfoField, { pdf: string; listing: string }> {
  const info = getBrandInfo(docId);
  const listing = getListing(docId);
  return {
    productName: { pdf: norm(info.productName), listing: norm(listing.productName) },
  };
}

// The stored pick for a field, but only while it still describes the answers now on
// screen. A pick made about values either source has since changed is not an answer
// to the question being asked now.
function livePick(docId: string, field: InfoField, picks: Picks): Source | null {
  const p = picks[field];
  if (!p) return null;
  const id = identities(docId)[field];
  return p.pdf === id.pdf && p.listing === id.listing ? p.source : null;
}

// Record the user's choice for one field, against the answers it was made about.
export function pickInfo(docId: string, field: InfoField, source: Source) {
  const entry = store.getEntry(docId);
  entry.snapshot = { ...entry.snapshot, [field]: { source, ...identities(docId)[field] } };
  store.notify(docId);
  writeState(docId, key(docId), entry.snapshot);
}

// Same value, said differently ("IKEA" / "ikea ") is not a disagreement.
const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
const same = (a: string, b: string) => norm(a) === norm(b);

export type Choice<T> = {
  // What each source answered. An empty string / false means "this source has
  // nothing to say", which is not a disagreement — it is silence.
  pdf: T;
  listing: T;
  // Both sources answered and they differ, so there are two candidates to show.
  // Stays true after the user picks: the picker keeps both on screen, with the
  // chosen one marked, so the choice can be changed without re-running anything.
  conflict: boolean;
  pick: Source | null;
  // A conflict the user hasn't settled yet. THIS is what holds Phase 1 open —
  // `conflict` alone would never clear.
  pending: boolean;
  // Which source the resolved value came from: the picked one, the only one that
  // answered, or — while a conflict is unresolved — the manual, which is what this
  // app read before listings existed. Null when neither source answered.
  winner: Source | null;
  value: T;
};

function choose<T extends string | boolean>(
  pdf: T,
  listing: T,
  differ: boolean,
  pick: Source | null,
): Choice<T> {
  const hasPdf = !!pdf;
  const hasListing = !!listing;
  const conflict = hasPdf && hasListing && differ;
  const chosen = conflict ? pick : null;
  let winner: Source | null = null;
  if (hasPdf) winner = chosen ?? 'pdf';
  else if (hasListing) winner = 'listing';
  return {
    pdf,
    listing,
    conflict,
    pick: chosen,
    pending: conflict && !chosen,
    winner,
    value: winner === 'listing' ? listing : pdf,
  };
}

// While either source is being re-read, its store still holds the PREVIOUS run's
// answer — nothing is overwritten until the new one lands. So a disagreement seen
// now is one between values that are already being replaced: an answered question
// about answers nobody is claiming any more. There is nothing to ask until both
// sources have spoken.
const rereading = (docId: string) => getBrandInfo(docId).detecting || getListing(docId).detecting;

// Whether a field's two sources disagree, straight off the stores.
function conflicts(docId: string): Record<InfoField, boolean> {
  const info = getBrandInfo(docId);
  const listing = getListing(docId);
  const picks = store.get(docId);
  const of = (f: InfoField, pdf: string | boolean, l: string | boolean, differ: boolean) =>
    !!pdf && !!l && differ && !livePick(docId, f, picks);
  return {
    productName: of(
      'productName', info.productName, listing.productName,
      !same(info.productName, listing.productName),
    ),
  };
}

// The gate Phase 1 and "Run all" read: is the user still owed a decision? Not a
// hook, so the orchestrator can ask between phases.
export function hasUnpickedConflicts(docId: string): boolean {
  return !rereading(docId) && INFO_FIELDS.some((f) => conflicts(docId)[f]);
}

export type ResolvedInfo = {
  brand: Choice<string>;
  productName: Choice<string>;
  // True while either field is still waiting on the user: Phase 1's gate.
  blocked: boolean;
};

// The resolved three facts, plus what is still unresolved. One hook so a component
// re-renders on a change to either source or to the picks.
export function useResolvedInfo(docId: string): ResolvedInfo {
  const info = useBrand(docId);
  const listing = useListing(docId);
  const picks = store.useSnapshot(docId);
  return useMemo(() => {
    const brand = choose(info.brand, '', false, null);
    const productName = choose(
      info.productName, listing.productName,
      !same(info.productName, listing.productName),
      livePick(docId, 'productName', picks),
    );
    return {
      brand,
      productName,
      // Not while the answers are being re-read: the disagreement on screen is the
      // last run's, between values about to be replaced.
      blocked:
        !info.detecting && !listing.detecting &&
        productName.pending,
    };
    // `livePick` reads the same two stores these values come from, so every input to
    // it is already a dependency.
  }, [
    docId,
    info.brand, info.productName, info.detecting,
    listing.productName, listing.detecting,
    picks,
  ]);
}
